import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import { ScopeOptionsT, SelectionBaseT } from 'business/definitions';
import {
    getExpandedRowIds,
    getFlatTreeNodeIdsFromSelection,
    getNodeIdsFromSelection,
    getSelectionTree,
    getSelectionTreeFromFlatTree,
    isFlatTree,
    selectTreeNode,
} from 'helpers/treeHelpers';
import SelectableTree from 'ui/Tree/SelectableTree';
import SelectAllFilter from './SelectAllFilter';
import { cloneDeep } from 'lodash';

const StyDialog = styled(Dialog, {
    name: 'StyDialog',
})({
    '.MuiPaper-root': {
        height: '100%',
    },
});

const StyDialogContent = styled(DialogContent, {
    name: 'StyDialog',
})({
    display: 'flex',
    flexDirection: 'column',
});

type Props = {
    onClose: () => void;
    title: string;
    tree: TreeT;
    defaultSelection: SelectionBaseT[];
    submit?: (arg1: SelectionBaseT[]) => void;
    show: boolean;
    canEdit?: boolean;
    scopeOptions: ScopeOptionsT[];
    showFilterPrefixOnDialogTitle?: boolean;
};

const TreeModalPicker = ({
    tree,
    defaultSelection,
    title,
    show,
    canEdit = true,
    onClose,
    submit = () => {},
    scopeOptions,
    showFilterPrefixOnDialogTitle = true,
}: Props) => {
    const { t } = useTranslation();
    let dialogTitle = title;
    if (showFilterPrefixOnDialogTitle) {
        dialogTitle = `${t('treeTableModal.filterOn')} ${title}`;
    }
    const isTreeFlatTree = useMemo(() => isFlatTree(tree), [tree]);
    const treeForScopes = useMemo(() => cloneDeep(tree), [tree]);

    // we're filtering the children to avoid label duplications and value
    // we can't ignore them sooner because we need the keys for the scopes
    useEffect(() => {
        if (tree && isTreeFlatTree) {
            tree.children = Array.from(
                new Map(
                    tree.children.map((child) => [
                        `${child.label}-${child.value}`,
                        child,
                    ]),
                ).values(),
            ).sort((a, b) => a.label.localeCompare(b.label));
        }
    }, [tree, isTreeFlatTree]);

    const mainSelection = useMemo(
        () =>
            defaultSelection.filter((val) => !val.value?.startsWith('scope:')),
        [defaultSelection],
    );

    const initialSelectedNodeIds: number[] = useMemo(
        () =>
            isTreeFlatTree
                ? getFlatTreeNodeIdsFromSelection(tree, mainSelection)
                : getNodeIdsFromSelection(tree, mainSelection),
        [tree, mainSelection],
    );

    const initialScopeSelection = useMemo(
        () => defaultSelection.filter((val) => val.value?.startsWith('scope:')),
        [defaultSelection],
    );

    const [selectedNodeIds, setSelectedNodeIds] = useState(
        initialSelectedNodeIds,
    );
    const [scopeSelection, setScopeSelection] = useState(initialScopeSelection);

    const getSelection = useCallback(
        () =>
            isTreeFlatTree
                ? getSelectionTreeFromFlatTree(tree, selectedNodeIds)
                : getSelectionTree(tree, selectedNodeIds),
        [selectedNodeIds, tree, isTreeFlatTree],
    );

    const defaultExpandedIds = useMemo(
        () => getExpandedRowIds(tree, mainSelection),
        [mainSelection, tree],
    );

    const handleNodeSelection = (node: TreeT) => {
        selectTreeNode(tree, node, setSelectedNodeIds);
        setScopeSelection([]);
    };

    const resetSelections = useCallback(() => {
        setSelectedNodeIds(initialSelectedNodeIds);
        setScopeSelection(initialScopeSelection);
    }, [initialSelectedNodeIds, initialScopeSelection]);

    useEffect(() => {
        // needed to update the selection when the user remove an item directly in the textfield
        setSelectedNodeIds(initialSelectedNodeIds);
        setScopeSelection(initialScopeSelection);
    }, [JSON.stringify(defaultSelection)]);

    return (
        <StyDialog
            open={show}
            maxWidth="md"
            onClose={(_event, reason) => {
                if (reason !== 'backdropClick') {
                    onClose();
                }
            }}
            fullWidth
            disableEscapeKeyDown
        >
            <DialogTitle variant="h5">{dialogTitle}</DialogTitle>

            <StyDialogContent>
                {!!(scopeOptions && scopeOptions.length === 1) && (
                    <>
                        <SelectAllFilter
                            isScopeSelectionActivated={
                                scopeSelection.length > 0
                            }
                            scopeOption={scopeOptions[0]}
                            setSelections={(scopes) => {
                                setScopeSelection(scopes);
                                setSelectedNodeIds([]);
                            }}
                            tree={treeForScopes}
                        />
                        <Divider sx={{ margin: '1rem 0 1rem 0' }}>OU</Divider>
                    </>
                )}
                <SelectableTree
                    withSearch
                    withSelectAll={canEdit}
                    tree={tree}
                    selectedNodeIds={selectedNodeIds}
                    setSelectedNodeIds={(fields) => {
                        setSelectedNodeIds(fields);
                        if (fields.length) {
                            setScopeSelection([]);
                        }
                    }}
                    defaultExpandedIds={defaultExpandedIds}
                    handleNodeSelect={handleNodeSelection}
                    isFlatTree={isTreeFlatTree}
                />
            </StyDialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        onClose();
                        resetSelections();
                    }}
                >
                    {t(canEdit ? 'common.cancel' : 'common.close')}
                </Button>
                {canEdit && (
                    <Button
                        data-testid="e2e-submit-tree-table"
                        onClick={() =>
                            submit([...getSelection(), ...scopeSelection])
                        }
                    >
                        {t('common.confirm')}
                    </Button>
                )}
            </DialogActions>
        </StyDialog>
    );
};

export default TreeModalPicker;
